import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { InlineMessage } from '../ui/InlineMessage';
import {
  integrationLimitMessageKey,
  isAtIntegrationLimit,
  type IntegrationCapacity,
} from './integrationLimitModel';

interface Props {
  capacity: IntegrationCapacity | null | undefined;
  /** Platform admin viewing this store. The customer sentence stays; this adds where to raise the plan. */
  viewingAsAdmin?: boolean;
}

/**
 * Shown wherever a new integration would start, once the plan's allowance is full.
 * Renders nothing while another integration is still allowed, including while the
 * capacity request has not returned (the server still refuses the start).
 */
export const IntegrationLimitNotice: React.FC<Props> = ({ capacity, viewingAsAdmin }) => {
  const { t } = useAppTranslation();
  const message = integrationLimitMessageKey(capacity);
  if (!isAtIntegrationLimit(capacity) || !message) return null;

  return (
    <InlineMessage severity="warning">
      <Typography variant="subtitle2" component="p" sx={{ mb: 0.5 }}>
        {t('integrations.integrationLimit.title')}
      </Typography>
      <Typography variant="body2">{t(message.key, message.params)}</Typography>
      {viewingAsAdmin && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t('integrations.integrationLimit.adminNote')}
        </Typography>
      )}
      <Box sx={{ mt: 1.5 }}>
        <Button component={RouterLink} to="/app/billing" variant="contained" size="small">
          {t('integrations.integrationLimit.upgrade')}
        </Button>
      </Box>
    </InlineMessage>
  );
};
